"use client";

/* eslint-disable react/jsx-no-constructed-context-values */
import React from "react";
import { sendGTMEvent } from "@next/third-parties/google";

import { Order } from "../models/orders";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[];
  consentUpdate(consent: object): void;
};

declare const window: WindowWithDataLayer;

interface TagManagerContextType {
  sendInscriptionEvent: () => void;
  sendInscriptionConfirmedEvent: () => void;
  sendNewsletterInscriptionEvent: () => void;
  sendPackItemViewEvent: ({ name, value, currency }:{ name: string, value:number, currency:string}) => void;
  sendPackListViewEvent: () => void;
  sendPurchaseEvent: (order: Order) => void;
  sendMarketingConsent: (consent:boolean) => void;
}

const TagManagerContext = React.createContext<TagManagerContextType>({
  sendInscriptionEvent: () => null,
  sendInscriptionConfirmedEvent: () => null,
  sendNewsletterInscriptionEvent: () => null,
  sendPackItemViewEvent: ({ name, value, currency }:{ name: string, value:number, currency:string}) => null,
  sendPackListViewEvent: () => null,
  sendPurchaseEvent: (order: Order) => null,
  sendMarketingConsent: (consent:boolean) => null,
});

const TagManagerProvider: React.FC<{children: React.ReactNode;}> = ({ children }) => {
  const sendEvent = (event?: string, data?: object) => {
    sendGTMEvent({
      event,
      ...data,
    });
  };
  const sendInscriptionEvent = () => sendEvent("sign_up");
  const sendInscriptionConfirmedEvent = () => sendEvent("sign_up_confirmed");
  const sendNewsletterInscriptionEvent = () => sendEvent("newsletter_subscribe");
  const sendPackItemViewEvent = ({ name, value, currency }) => sendEvent("pack_item_view", { packName: name, packValue: value, packCurrency: currency });
  const sendPackListViewEvent = () => sendEvent("pack_list_view");
  const sendPurchaseEvent = (order: Order) => {
    if (localStorage.getItem("purchase") === order.orderId) return;
    sendEvent("purchase", {
      transactionId: order.orderId,
      currency: order.currency,
      value: order.netPrice,
      items: [{
        itemName: order.pack.name,
        quantity: 1,
        price: order.netPrice,
        currency: order.currency,
      }],
    });
    if (order.firstPurchase) {
      sendEvent("first_purchase", {
        transactionId: order.orderId,
        currency: order.currency,
        value: order.netPrice,
      });
    }
    localStorage.setItem("purchase", order.orderId);
  };
  const sendMarketingConsent = (consent: boolean) => {
    window.consentUpdate({
      analytics_storage: consent ? "granted" : "denied",
      ad_storage: consent ? "granted" : "denied",
      ad_user_data: consent ? "granted" : "denied",
      ad_personalization: consent ? "granted" : "denied",
    });
    sendEvent("consent_update");
  };

  return (
    <TagManagerContext.Provider value={{
      sendInscriptionEvent,
      sendInscriptionConfirmedEvent,
      sendNewsletterInscriptionEvent,
      sendPackItemViewEvent,
      sendPackListViewEvent,
      sendPurchaseEvent,
      sendMarketingConsent,
    }}
    >
      {children}
    </TagManagerContext.Provider>
  );
};

const useTagManager = () => React.useContext(TagManagerContext);

export default TagManagerContext;
export { TagManagerProvider, useTagManager };
